import { useCallback, useEffect, useMemo, useState } from 'react';
import { storageApiRef, useApi } from '@backstage/core-plugin-api';

/**
 * Whether the reader looks at the catalog as a table or as cards.
 *
 * Kept in the app's own storage rather than in the address, because it is a
 * preference and not a question: a reader who chose cards wants cards the next
 * time they open the catalog from the sidebar, which carries no address of
 * theirs, and a link pasted into a chat should not change how the next reader
 * likes to read.
 */

export type ListLayout = 'table' | 'cards';

const BUCKET = 'catalog-list';
const KEY = 'layout';

const asLayout = (value: unknown): ListLayout =>
  value === 'cards' ? 'cards' : 'table';

export function useViewPreference(): [ListLayout, (next: ListLayout) => void] {
  const storageApi = useApi(storageApiRef);
  const bucket = useMemo(() => storageApi.forBucket(BUCKET), [storageApi]);
  const [layout, setLayout] = useState<ListLayout>(() =>
    asLayout(bucket.snapshot<ListLayout>(KEY).value),
  );

  useEffect(() => {
    const subscription = bucket
      .observe$<ListLayout>(KEY)
      .subscribe(snapshot => setLayout(asLayout(snapshot.value)));
    return () => subscription.unsubscribe();
  }, [bucket]);

  const choose = useCallback(
    (next: ListLayout) => {
      setLayout(next);
      bucket.set(KEY, next).catch(() => undefined);
    },
    [bucket],
  );

  return [layout, choose];
}
